<template>
  <v-app id="admin-app">
    <v-navigation-drawer
      app
      v-model="drawer"
      :width="drawerWidth"
      :mini-variant="isMini"
      mini-variant-width="80"
      :stateless="!$vuetify.breakpoint.mobile"
      class="admin-drawer"
      :class="{ 'admin-drawer--mini': isMini }"
    >
      <!-- Poignée de redimensionnement -->
      <div v-if="!$vuetify.breakpoint.mobile && !isMini" class="admin-drawer__resize-handle" @mousedown="startResize"></div>

      <!-- En-tête : marque + compagnie -->
      <div class="admin-drawer__header">
        <div class="admin-drawer__brand">
          <img src="../assets/urbanGo.png" alt="urbanGo" />
        </div>

        <div class="admin-drawer__company" :title="isMini ? companyName : null">
          <div class="admin-drawer__company-logo">
            <img v-if="logoCompagnieUrl" :src="logoCompagnieUrl" alt="" />
            <span v-else>{{ companyInitial }}</span>
          </div>
          <div class="admin-drawer__company-info">
            <div class="admin-drawer__company-name">{{ companyName }}</div>
            <div class="admin-drawer__company-sub">{{ $t('navigation.administration') }}</div>
          </div>
        </div>
      </div>

      <!-- Menu -->
      <nav class="admin-nav">
        <button
          type="button"
          class="admin-nav__item"
          :class="{ 'admin-nav__item--active': isActive('/tableauBord') }"
          :title="isMini ? $t('navigation.dashboard') : null"
          @click="goTo('/tableauBord')"
        >
          <v-icon class="admin-nav__mdi" size="20">mdi-view-dashboard-outline</v-icon>
          <span class="admin-nav__label">{{ $t('navigation.dashboard') }}</span>
        </button>

        <div class="admin-nav__section">Menu</div>

        <div v-for="menu in buildComponentFromFunctionnalities" :key="menu.title">
          <!-- Lien simple -->
          <button
            v-if="!menu.items"
            type="button"
            class="admin-nav__item"
            :class="{ 'admin-nav__item--active': isActive(menu.navigation) }"
            :title="isMini ? menu.title : null"
            @click="goTo(menu.navigation)"
          >
            <img :src="menu.url" alt="" class="admin-nav__icon" />
            <span class="admin-nav__label">{{ menu.title }}</span>
          </button>

          <!-- Groupe avec sous-menus -->
          <div v-else class="admin-nav__group">
            <button
              type="button"
              class="admin-nav__item"
              :class="{
                'admin-nav__item--open': openGroups[menu.title],
                'admin-nav__item--parent-active': isGroupActive(menu),
                'admin-nav__item--active': isMini && isGroupActive(menu),
              }"
              :title="isMini ? menu.title : null"
              @click="toggleGroup(menu.title)"
            >
              <img :src="menu.url" alt="" class="admin-nav__icon" />
              <span class="admin-nav__label">{{ menu.title }}</span>
              <span class="admin-nav__chevron"></span>
            </button>

            <transition name="admin-collapse">
              <div v-show="openGroups[menu.title] && !isMini" class="admin-nav__children">
                <button
                  v-for="child in menu.items"
                  :key="child.title"
                  type="button"
                  class="admin-nav__child"
                  :class="{ 'admin-nav__child--active': isActive(child.navigation) }"
                  @click="goTo(child.navigation)"
                >
                  {{ child.title }}
                </button>
              </div>
            </transition>
          </div>
        </div>
      </nav>

      <!-- Pied du drawer : utilisateur -->
      <template v-slot:append>
        <div
          class="admin-drawer__user"
          :title="isMini ? userFullName : null"
          @click="goToRoute({ name: 'userProfilCompagnieTransport' })"
        >
          <v-avatar size="36" class="admin-drawer__user-avatar">
            <v-img v-if="$store.state.pictureProfilUser != null" :src="$store.state.pictureProfilUser"></v-img>
            <span v-else>{{ userInitials }}</span>
          </v-avatar>
          <div class="admin-drawer__user-info">
            <div class="admin-drawer__user-name">{{ userFullName }}</div>
            <div class="admin-drawer__user-email">{{ $store.state.userAuthentified.email }}</div>
          </div>
        </div>
      </template>
    </v-navigation-drawer>

    <v-app-bar app flat color="white" height="68" class="admin-appbar">
      <button type="button" class="admin-appbar__toggle" @click="toggleDrawer">
        <span></span><span></span><span></span>
      </button>

      <div class="admin-appbar__heading">
        <div v-if="currentPage.section" class="admin-appbar__section">{{ currentPage.section }}</div>
        <div class="admin-appbar__title">{{ currentPage.title }}</div>
      </div>

      <v-spacer></v-spacer>

      <!-- Notifications -->
      <v-menu
        v-model="menuNotification"
        :close-on-content-click="false"
        :nudge-width="320"
        :nudge-bottom="8"
        offset-y
        left
        transition="slide-y-transition"
      >
        <template v-slot:activator="{ on, attrs }">
          <button type="button" class="admin-appbar__btn" v-bind="attrs" v-on="on">
            <v-icon color="#334155" size="22">mdi-bell-outline</v-icon>
            <span v-if="messages.length" class="admin-appbar__dot"></span>
          </button>
        </template>

        <div class="admin-pop">
          <div class="admin-pop__head">
            <div class="admin-pop__title">{{ $t('notifications.title') }}</div>
            <span v-if="messages.length" class="admin-pop__count">{{ messages.length }}</span>
          </div>

          <div v-if="messages.length == 0" class="admin-pop__empty">
            {{ $t('notifications.noNotifications') }}
          </div>

          <div v-else class="admin-pop__list">
            <div v-for="(mes, msg) in messages" :key="msg" class="admin-notif">
              <span class="admin-notif__dot"></span>
              <div>
                <div class="admin-notif__title">{{ mes.raisonSociale }}</div>
                <div class="admin-notif__text">{{ mes.message }}</div>
              </div>
            </div>
          </div>
        </div>
      </v-menu>

      <!-- Profil -->
      <v-menu
        v-model="menuProfil"
        :close-on-content-click="false"
        :nudge-width="260"
        :nudge-bottom="8"
        offset-y
        left
        transition="slide-y-transition"
      >
        <template v-slot:activator="{ on, attrs }">
          <button type="button" class="admin-appbar__profile" v-bind="attrs" v-on="on">
            <v-avatar size="34" class="admin-appbar__avatar">
              <v-img v-if="$store.state.pictureProfilUser != null" :src="$store.state.pictureProfilUser"></v-img>
              <span v-else>{{ userInitials }}</span>
            </v-avatar>
            <span class="admin-appbar__name">{{ $store.state.userAuthentified.prenoms }}</span>
          </button>
        </template>

        <div class="admin-pop">
          <div class="admin-pop__user">
            <v-avatar size="44" class="admin-appbar__avatar">
              <v-img v-if="$store.state.pictureProfilUser != null" :src="$store.state.pictureProfilUser"></v-img>
              <span v-else>{{ userInitials }}</span>
            </v-avatar>
            <div class="admin-pop__user-info">
              <div class="admin-pop__user-name">{{ userFullName }}</div>
              <div class="admin-pop__user-email">{{ $store.state.userAuthentified.email }}</div>
            </div>
          </div>

          <div class="admin-pop__menu">
            <button type="button" class="admin-pop__link" @click="goToRoute({ name: 'userProfilCompagnieTransport' })">
              <v-icon size="20" color="#475569">mdi-account-outline</v-icon>
              {{ $t('profile.myProfile') }}
            </button>
            <button type="button" class="admin-pop__link" @click="goToRoute({ name: 'resetPasswordCompagnieTransport' })">
              <v-icon size="20" color="#475569">mdi-lock-outline</v-icon>
              {{ $t('profile.changePassword') }}
            </button>

            <div class="admin-pop__lang">
              <span>{{ $t('profile.language') }}</span>
              <div class="admin-lang">
                <button
                  v-for="loc in availableLocales"
                  :key="loc.code"
                  type="button"
                  class="admin-lang__opt"
                  :class="{ 'admin-lang__opt--active': currentLocale === loc.code }"
                  @click="changeLocale(loc.code)"
                >
                  {{ loc.code.toUpperCase() }}
                </button>
              </div>
            </div>
          </div>

          <div class="admin-pop__foot">
            <button type="button" class="admin-pop__link admin-pop__link--danger" @click="dialogLogout = true">
              <v-icon size="20" color="#ef4444">mdi-logout</v-icon>
              {{ $t('profile.logout') }}
            </button>
          </div>
        </div>
      </v-menu>
    </v-app-bar>

    <!-- Confirmation de déconnexion -->
    <v-dialog v-model="dialogLogout" persistent max-width="400">
      <div class="admin-logout">
        <div class="admin-logout__icon">
          <v-icon color="#ef4444" size="32">mdi-logout</v-icon>
        </div>
        <div class="admin-logout__title">{{ $t('profile.confirmLogout') }}</div>
        <div class="admin-logout__text">{{ $t('profile.confirmLogoutMessage') }}</div>
        <div class="admin-logout__actions">
          <button type="button" class="admin-logout__btn" @click="dialogLogout = false">
            {{ $t('common.cancel') }}
          </button>
          <button type="button" class="admin-logout__btn admin-logout__btn--danger" @click="logout">
            {{ $t('profile.disconnect') }}
          </button>
        </div>
      </div>
    </v-dialog>

    <v-main class="admin-main">
      <router-view></router-view>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import {
  API_GET_DOCUMENT_URL,
  API_GET_DOCUMENT_BASE64,
  API_GET_FUNCTIONNALITY_BY_ROLE,
  HEADERS,
  LISTEN_URL_NOTIFICATION,
} from "../components/globalConfig/globalConstConfig";
import {
  FUNCTIONNALITY_MANAGE_OFFRE_VOYAGE,
  FUNCTIONNALITY_MANAGE_STATION,
  FUNCTIONNALITY_MANAGE_BUS,
  FUNCTIONNALITY_MANAGE_ADHESION,
  FUNCTIONNALITY_MANAGE_DOCUMENT,
  FUNCTIONNALITY_MANAGE_NOTIFICATION,
  FUNCTIONNALITY_MANAGE_BAGS,
  FUNCTIONALITY_MANAGE_CATEGORIE_VOYAGEUR,
  FUNCTIONALITY_MANAGE_REFERENCE,
  FUNCTIONNALITY_MANAGE_COUNTRY,
  FUNCTIONNALITY_MANAGE_TOWN,
  FUNCTIONNALITY_MANAGE_ACCOUNT,
  FUNCTIONNALITY_MANAGE_WALLET,
  FUNCTIONNALITY_MANAGE_ROLE_AND_PERMISSIONS,
  FUNCTIONNALITY_MANAGE_SUBSCRIPTION,
  FUNCTIONNALITY_MANAGE_PAYMENT,
  FUNCTIONNALITY_CHECK_TICKET,
  FUNCTIONNALITY_MANAGE_RESERVATION_STATION,
  FUNCTIONNALITY_MANAGE_STRATEGIE_BAGS,
} from "../components/globalConfig/constFunctionnalies";
import { showErrorMessage } from "@/modules/messages/messageProcess";

let sseClient;
export default {
  name: "EspaceUI",
  data() {
    return {
      menuProfil: false,
      menuNotification: false,
      dialogLogout: false,
      mini: false,
      drawer: !this.$vuetify.breakpoint.mobile,
      drawerWidth: 290,
      openGroups: {},
      isResizing: false,
      functionnalitiesListByUserRole: [],
      functionnalitiesListComponents: [],

      sseConnection: null,

      photoProfilObject: {
        data: {
          typeDocument: null,
        },
      },

      messages: [],

      logoCompagnieTransport: {
        data: {
          typeDocument: null,
          compagnieTransportRaisonSociale: null,
        },
      },

      photoProfilUrl: null,
      logoCompagnieUrl: null,

      availableLocales: [
        { code: 'fr', name: 'Français' },
        { code: 'en', name: 'English' }
      ],
    };
  },

  methods: {
    // Retrieve notification from sse server connect
    checkNotification() {
      sseClient = this.$sse.create({
        url: LISTEN_URL_NOTIFICATION("COMPAGNIE"),
        format: "json",
        withCredentials: false,
        polyfill: true,
      });

      // Événements diffusés à tous les clients de la compagnie de l'utilisateur
      // (le nom de la compagnie était écrit en dur : « KOUEVI CT_toutclient »)
      const companyEvent = `${this.$store.state.userAuthentified.compagnieTransportRaisonSociale}_toutclient`;
      sseClient.on(companyEvent, (event) => this.messages.push(event));

      sseClient
        .connect()
        .then((sse) => {
          console.log("We're connected!");
          console.log(sse);
        })
        .catch((err) => {
          // When this error is caught, it means the initial connection to the
          // events server failed.  No automatic attempts to reconnect will be made.
          console.error("Failed to connect to server", err);
        });

      /*this.sseConnection = this.$sse
        .create({
          url: LISTEN_URL_NOTIFICATION("COMPAGNIE"),
          format: "json",
          polyfill: true,
        })
        .on(`${this.$store.state.userAuthentified.compagnieTransportRaisonSociale}_toutclient`, (event) => this.messages.push(event))
        .on("error", (err) =>
          console.error("Failed to parse or lost connection:", err)
        )
        .connect()
        .catch((err) => console.error("Failed make initial connection:", err));*/
    },

    sseNotificationStatisticSeller() {
      sseClient = this.$sse.create({
        url: LISTEN_URL_NOTIFICATION("STATISTIQUE_COMPAGNIE"),
        format: "json",
        withCredentials: false,
        polyfill: true,
      });

      // Handle messages without a specific event
      sseClient.on("kouevi_ct_annuel", (event) => console.log(event.data));

      sseClient
        .connect()
        .then((sse) => {
          console.log("We're connected!");
          console.log(sse);
        })
        .catch((err) => {
          // When this error is caught, it means the initial connection to the
          // events server failed.  No automatic attempts to reconnect will be made.
          console.error("Failed to connect to server", err);
        });
    },

    checkNotificationProgram() {
      this.$sse
        .create({
          url: LISTEN_URL_NOTIFICATION("COMPAGNIE_PROGRAMME"),
          format: "json",
          polyfill: true,
        })
        .on("Prog 003_programme", (event) => this.messages.push(event))
        .on("error", (err) =>
          console.error("Failed to parse or lost connection:", err)
        )
        .connect()
        .catch((err) => console.error("Failed make initial connection:", err));
    },

    checkNotificationSystem() {
      this.$sse
        .create({
          url: LISTEN_URL_NOTIFICATION("SYSTEM"),
          format: "json",
          polyfill: true,
        })
        .on("tout_utilisateur", (event) => this.messages.push(event))
        .on("error", (err) =>
          console.error("Failed to parse or lost connection:", err)
        )
        .connect()
        .catch((err) => console.error("Failed make initial connection:", err));
    },

    // Get user profil picture as base64 and store it.
    async getBase64PhotoProfil() {
      this.photoProfilObject.data.typeDocument = "PHOTO_PROFIL";
      await axios
        .post(API_GET_DOCUMENT_BASE64, this.photoProfilObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200 && response.data.status.code == 800) {
            const base64 = "data:image/png;base64," + response.data.item.content;
            this.$store.commit("UPDATE_PICTURE_PROFIL", base64);
          }
        })
        .catch(() => {});
    },

    // Get user profil picture.
    async getUrlPhotoProfil() {
      this.photoProfilObject.data.typeDocument = "PHOTO_PROFIL";
      this.loading = true;
      await axios
        .post(API_GET_DOCUMENT_URL, this.photoProfilObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200 && response.data.status.code == 800) {
            this.photoProfilUrl = response.data.item.url;
            this.$store.commit("UPDATE_PICTURE_PROFIL", response.data.item.url);
          }
          // Pas de photo : on garde photoProfilUrl = null (image par défaut)
        })
        .catch((e) => {
          this.errorMsg = e;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async getUrlLogoCompagnie() {
      this.logoCompagnieTransport.data.typeDocument = "LOGO";
      this.logoCompagnieTransport.data.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      this.loading = true;
      await axios
        .post(API_GET_DOCUMENT_URL, this.logoCompagnieTransport, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              //showErrorMessage();
            } else {
              this.logoCompagnieUrl = response.data.item.url;
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // RECUPERER LES DONNEES DE L'UTILISATEUR CONNECTÉ DEPUIS VUEX
    // Note: Cette méthode n'est plus nécessaire car l'utilisateur est déjà dans Vuex
    // Conservée pour compatibilité mais ne fait plus rien
    storeSessionUser() {
      // L'utilisateur est déjà stocké dans Vuex via l'action setUser lors du login
      // Pas besoin de récupérer depuis localStorage
    },

    //RECUPERER LA LISTE DES FONCTIONNALITÉS PAR ROLE UTILISATEURS
    async getAllFunctionnalitiesByUserRole() {
      this.storeSessionUser();
      await axios
        .post(
          API_GET_FUNCTIONNALITY_BY_ROLE,
          { data: { code: this.$store.state.userAuthentified.roleCode } },
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.functionnalitiesListByUserRole = response.data.items;
            }
          }
        })
        .catch((e) => {
          console.log(e);
        });
    },

    // Bloquer la session de l'utilisateur
    // Note: Plus besoin de nettoyer localStorage car le token est dans Vuex
    destroyLocalSession() {
      // Le token est maintenant géré uniquement par Vuex
      // Il sera automatiquement supprimé par DESTROY_SESSION_USER
    },

    // METHODE PERMETTANT À UN UTILISATEUR DE SE DECONNECTER
    logout() {
      this.destroyLocalSession();
      this.$store.commit("DESTROY_SESSION_USER");
      this.$router.replace("/");
    },

    // Méthode pour changer la langue
    changeLocale(locale) {
      this.$i18n.locale = locale;
      localStorage.setItem('locale', locale);
    },

    checkIfFunctionnalityExist(functionnality) {
      var exist = false;
      this.functionnalitiesListByUserRole.forEach((element) => {
        if (element.funct == functionnality) {
          exist = true;
        }
      });
      return exist;
    },

    // NAVIGATION DEPUIS LE DRAWER
    goTo(path) {
      this.goToRoute({ path });
    },

    goToRoute(location) {
      this.menuProfil = false;
      this.$router.push(location).catch(() => {});
      if (this.$vuetify.breakpoint.mobile) {
        this.drawer = false;
      }
    },

    isActive(path) {
      return decodeURI(this.$route.path) === path;
    },

    isGroupActive(menu) {
      return menu.items.some((child) => this.isActive(child.navigation));
    },

    toggleGroup(title) {
      // En mode réduit, un clic sur un groupe rouvre le drawer sur ce groupe
      if (this.isMini) {
        this.mini = false;
        this.$set(this.openGroups, title, true);
        return;
      }
      this.$set(this.openGroups, title, !this.openGroups[title]);
    },

    // MOBILE : OUVRE / FERME LE DRAWER. ÉCRAN LARGE : BASCULE EN MODE ICÔNES
    toggleDrawer() {
      if (this.$vuetify.breakpoint.mobile) {
        this.drawer = !this.drawer;
      } else {
        this.drawer = true;
        this.mini = !this.mini;
      }
    },

    // OUVRE LE GROUPE QUI CONTIENT LA PAGE COURANTE
    openActiveGroup() {
      this.buildComponentFromFunctionnalities.forEach((menu) => {
        if (menu.items && this.isGroupActive(menu)) {
          this.$set(this.openGroups, menu.title, true);
        }
      });
    },

    // Méthodes pour redimensionner le drawer
    startResize() {
      this.isResizing = true;
      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    },

    handleResize(e) {
      if (!this.isResizing) return;
      
      const newWidth = e.clientX;
      
      // Limiter la largeur entre 250px et 500px
      if (newWidth >= 250 && newWidth <= 500) {
        this.drawerWidth = newWidth;
      }
    },

    stopResize() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },

    isMini() {
      return this.mini && !this.$vuetify.breakpoint.mobile;
    },

    companyName() {
      return this.$store.state.userAuthentified.compagnieTransportRaisonSociale || "";
    },

    companyInitial() {
      return this.companyName.charAt(0).toUpperCase();
    },

    userFullName() {
      const user = this.$store.state.userAuthentified;
      return [user.prenoms, user.nom].filter(Boolean).join(" ");
    },

    userInitials() {
      const user = this.$store.state.userAuthentified;
      return [user.prenoms, user.nom]
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },

    // TITRE DE LA PAGE COURANTE AFFICHÉ DANS L'APP BAR
    currentPage() {
      if (this.isActive("/tableauBord")) {
        return { section: null, title: this.$t("navigation.dashboard") };
      }
      for (const menu of this.buildComponentFromFunctionnalities) {
        if (!menu.items && this.isActive(menu.navigation)) {
          return { section: null, title: menu.title };
        }
        const child = (menu.items || []).find((item) => this.isActive(item.navigation));
        if (child) {
          return { section: menu.title, title: child.title };
        }
      }
      return { section: null, title: this.$t("navigation.administration") };
    },

    buildComponentFromFunctionnalities() {
      var globalFunctionnalities = [];

      this.functionnalitiesListByUserRole.forEach((element) => {
        if (element.code == FUNCTIONNALITY_MANAGE_SUBSCRIPTION) {
          var manageSubscription = {
            title: this.$t('menu.subscription.title'),
            url: require("@/assets/icons/calendar-check.png"),
            items: [
              {
                title: this.$t('menu.subscription.create'),
                navigation: "/creerModeAbonnement",
              },
              {
                title: this.$t('menu.subscription.list'),
                navigation: "/selectionnerModeAbonnementEtPaiement",
              },
            ],
          };
          globalFunctionnalities.push(manageSubscription);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_PAYMENT) {
          var managePayment = {
            title: this.$t('menu.payment.title'),
            url: require("@/assets/icons/credit-card.png"),
            items: [
              {
                title: this.$t('menu.payment.create'),
                navigation: "/creerModePaiement",
              },
              {
                title: this.$t('menu.payment.list'),
                navigation: "/PaymentToolList",
              },
            ],
          };
          globalFunctionnalities.push(managePayment);
        }

        if (element.code == FUNCTIONALITY_MANAGE_REFERENCE) {
          var manageReference = {
            title: this.$t('menu.reference.title'),
            url: require("@/assets/icons/layout-dashboard.png"),
            items: [
              {
                title: this.$t('menu.reference.family'),
                navigation: "/reference_famille",
              },
              {
                title: this.$t('menu.reference.create'),
                navigation: "/CreerReference",
              },
            ],
          };
          globalFunctionnalities.push(manageReference);
        }

        if (element.code == FUNCTIONALITY_MANAGE_CATEGORIE_VOYAGEUR) {
          var manageCategorie = {
            title: this.$t('menu.category.title'),
            url: require("@/assets/icons/chart-bar-stacked.png"),
            navigation: "/select_category",
          };
          globalFunctionnalities.push(manageCategorie);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_STATION) {
          var manageStation = {
            title: this.$t('menu.station.title'),
            url: require("@/assets/icons/presentation.png"),
            navigation: "/creerGareCompagnieTransport",
          };
          globalFunctionnalities.push(manageStation);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_BUS) {
          var manageBus = {
            title: this.$t('menu.bus.title'),
            url: require("@/assets/icons/bus.png"),
            navigation: "/associerBusCompagnieTransport",
          };
          globalFunctionnalities.push(manageBus);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_OFFRE_VOYAGE) {
          var manageOffreVoyage = {
            title: this.$t('menu.travelOffer.title'),
            url: require("@/assets/icons/map-pin-house.png"),
            items: [
              {
                title: this.$t('menu.travelOffer.create'),
                navigation: "/creerOffreVoyage",
              },
              {
                title: this.$t('menu.travelOffer.list'),
                navigation: "/selectionnerOffreVoyage",
              },
              {
                title: this.$t('menu.travelOffer.plan'),
                navigation: "/planifierOffreVoyage",
              },
            ],
          };
          globalFunctionnalities.push(manageOffreVoyage);
        }

        if (element.code == FUNCTIONNALITY_CHECK_TICKET) {
          var checkTicket = {
            title: this.$t('menu.ticket.title'),
            url: require("@/assets/icons/scan-barcode.png"),
            items: [
              {
                title: this.$t('menu.ticket.verify'),
                navigation: "/verifierBilletReservation",
              },
              {
                title: this.$t('menu.ticket.confirm'),
                navigation: "/confirmerBilletReservation",
              },
            ],
          };
          globalFunctionnalities.push(checkTicket);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_RESERVATION_STATION) {
          var manageReservationTicket = {
            title: this.$t('menu.reservation.title'),
            url: require("@/assets/icons/tickets.png"),
            items: [
              {
                title: this.$t('menu.reservation.book'),
                navigation: "/selectionnerOffreVoyageReservation",
              },
              {
                title: this.$t('menu.reservation.view'),
                navigation: "/selectionnerReservationBillet",
              },
            ],
          };
          globalFunctionnalities.push(manageReservationTicket);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_BAGS) {
          var manageBags = {
            title: this.$t('menu.luggage.title'),
            url: require("@/assets/icons/handbag.png"),
            items: [
              {
                title: this.$t('menu.luggage.register'),
                navigation: "/SearchTicket",
              },
              {
                title: this.$t('menu.luggage.track'),
                navigation: "/SuiviDesBagages",
              },
            ],
          };
          globalFunctionnalities.push(manageBags);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_COUNTRY) {
          var manageCountry = {
            title: this.$t('menu.country.title'),
            url: require("@/assets/icons/house.png"),
            navigation: "/creerPays",
          };

          globalFunctionnalities.push(manageCountry);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_TOWN) {
          var manageTown = {
            title: this.$t('menu.city.title'),
            url: require("@/assets/icons/building-2.png"),
            navigation: "/creerVille",
          };

          globalFunctionnalities.push(manageTown);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_ACCOUNT) {
          var manageAccount = {
            title: this.$t('menu.account.title'),
            url: require("@/assets/icons/users.png"),
            items: [
              {
                title: this.$t('menu.account.create'),
                navigation: "/create_user",
              },
              {
                title: this.$t('menu.account.list'),
                navigation: "/selectionnerUtilisateur",
              },
            ],
          };

          globalFunctionnalities.push(manageAccount);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_ROLE_AND_PERMISSIONS) {
          var createRoleAndFunctionnality = {
            title: this.$t('menu.role.title'),
            url: require("@/assets/icons/octagon-minus.png"),
            items: [
              {
                title: this.$t('menu.role.addFunctionality'),
                navigation: "/permissions",
              },
              {
                title: this.$t('menu.role.addRole'),
                navigation: "/creerRoleUtilisateur",
              },
            ],
          };

          globalFunctionnalities.push(createRoleAndFunctionnality);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_STRATEGIE_BAGS) {
          var createStrategieBagageFunctionality = {
            title: this.$t('menu.luggageStrategy.title'),
            url: require("@/assets/icons/chart-column-big.png"),
            navigation: "/creerStrategieBagage",
          };
          

          globalFunctionnalities.push(createStrategieBagageFunctionality);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_DOCUMENT) {
          var manageDocumentFunctionality = {
            title: this.$t('menu.document.title'),
            url: require("@/assets/icons/files.png"),
            items: [
              {
                title: this.$t('menu.document.transportCertificate'),
                navigation: "/attestationTransport",
              },
              {
                title: this.$t('menu.document.companyLogo'),
                navigation: "/imageCompagnieTransport",
              },
              {
                title: this.$t('menu.document.list'),
                navigation: "/Liste des documents",
              },
            ],
          };

          globalFunctionnalities.push(manageDocumentFunctionality);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_NOTIFICATION) {
          var functionManageNotification = {
            title: this.$t('menu.notification.title'),
            url: require("@/assets/icons/bell-ring.png"),
            items: [
              {
                title: this.$t('menu.notification.send'),
                navigation: "/send-notification",
              },
              {
                title: this.$t('menu.notification.list'),
                navigation: "/all-notifications-sended",
              },
            ],
          };

          globalFunctionnalities.push(functionManageNotification);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_WALLET) {
          var functionGestionCaisse = {
            title: this.$t('menu.wallet.title'),
            url: require("@/assets/icons/credit-card.png"),
            items: [
              {
                title: this.$t('menu.wallet.create'),
                navigation: "/create-new-transaction",
              },
              {
                title: this.$t('menu.wallet.list'),
                navigation: "/list-transactions",
              },
            ],
          };

          globalFunctionnalities.push(functionGestionCaisse);
        }

        if (element.code == FUNCTIONNALITY_MANAGE_ADHESION) {
          var adhesionFunction = {
            title: this.$t('menu.adhesion.title'),
            url: require("@/assets/icons/circle-plus.png"),
            items: [
              {
                title: this.$t('menu.adhesion.create'),
                navigation: "/CreerAdhesionAdmin",
              },
              {
                title: this.$t('menu.adhesion.list'),
                navigation: "/selectionnerDemandeAdhesionCompagnie",
              },
            ],
          };

          globalFunctionnalities.push(adhesionFunction);
        }

        // ==============================================================================================================================================//
      });

      return globalFunctionnalities;
    },
  },

  watch: {
    $route() {
      this.openActiveGroup();
    },

    functionnalitiesListByUserRole() {
      this.openActiveGroup();
    },
  },

  mounted() {
    this.getBase64PhotoProfil();
    this.getUrlPhotoProfil();
    this.getUrlLogoCompagnie();
    this.getAllFunctionnalitiesByUserRole();
    //this.checkNotification();
    this.sseNotificationStatisticSeller();
    //this.checkNotificationProgram();
    //this.checkNotificationSystem();
    //this.sseNotificationStatisticSeller();
  },

  beforeDestroy() {
    // Nettoyer les event listeners du resize
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
    
    // Déconnecter SSE
    if (sseClient) {
      sseClient.disconnect();
    }
  },
};
</script>

<style lang="scss" scoped>
$green: #159e72;
$green-dark: #11845f;
$teal: #079992;
$navy: #0c2461;
$navy-deep: #081a47;
$ink: #0f172a;
$muted: #475569;
$line: rgba(15, 23, 42, 0.08);
$soft: #f8fafc;
$danger: #ef4444;

#admin-app {
  background: $soft;
}

.admin-main {
  background: $soft;
  min-height: 100vh;
}

button {
  font-family: "Montserrat", sans-serif;
}

/* ============ DRAWER ============ */
.admin-drawer {
  background: linear-gradient(180deg, $navy 0%, $navy-deep 100%) !important;
  border: none !important;

  ::v-deep .v-navigation-drawer__border {
    display: none;
  }

  ::v-deep .v-navigation-drawer__content {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.15);
      border-radius: 3px;
    }
  }
}

.admin-drawer__resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  cursor: ew-resize;
  z-index: 10;
  transition: background 0.2s ease;

  &:hover,
  &:active {
    background: rgba(21, 158, 114, 0.5);
  }
}

.admin-drawer__header {
  padding: 20px 16px 8px;
}

.admin-drawer__brand {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  margin-bottom: 18px;
  background: #ffffff;
  border-radius: 14px;

  img {
    height: 46px;
    width: auto;
  }
}

.admin-drawer__company {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}

.admin-drawer__company-logo {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $green, $teal);
  color: #ffffff;
  font-weight: 900;
  font-size: 17px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #ffffff;
  }
}

.admin-drawer__company-info {
  min-width: 0;
}

.admin-drawer__company-name {
  font-size: 14px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-drawer__company-sub {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.55);
}

/* ============ MENU ============ */
.admin-nav {
  padding: 12px 12px 24px;
}

.admin-nav__section {
  padding: 18px 12px 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}

.admin-nav__item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  margin-bottom: 2px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #ffffff;

    .admin-nav__icon {
      opacity: 1;
    }
  }

  &--active {
    background: $green;
    color: #ffffff;
    box-shadow: 0 6px 16px rgba(21, 158, 114, 0.35);

    &:hover {
      background: $green;
    }

    .admin-nav__icon,
    .admin-nav__mdi {
      opacity: 1;
    }
  }

  &--parent-active {
    color: #ffffff;

    .admin-nav__icon {
      opacity: 1;
    }
  }
}

.admin-nav__icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.admin-nav__mdi {
  color: inherit !important;
  opacity: 0.85;
}

.admin-nav__label {
  flex: 1;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

.admin-nav__chevron {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  margin-right: 4px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  opacity: 0.6;
  transform: rotate(-45deg);
  transition: transform 0.2s ease;
}

.admin-nav__item--open .admin-nav__chevron {
  transform: rotate(45deg);
}

.admin-nav__children {
  position: relative;
  margin: 2px 0 8px 22px;
  padding-left: 14px;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
}

.admin-nav__child {
  position: relative;
  width: 100%;
  display: block;
  padding: 8px 12px;
  margin-bottom: 2px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12.5px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }

  &--active {
    color: #ffffff;
    background: rgba(21, 158, 114, 0.22);

    &::before {
      content: "";
      position: absolute;
      left: -15px;
      top: 8px;
      bottom: 8px;
      width: 2px;
      border-radius: 2px;
      background: $green;
    }
  }
}

.admin-collapse-enter-active,
.admin-collapse-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.admin-collapse-enter,
.admin-collapse-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Pied du drawer */
.admin-drawer__user {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.admin-drawer__user-avatar {
  flex-shrink: 0;
  background: linear-gradient(135deg, $green, $teal);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
}

.admin-drawer__user-info {
  min-width: 0;
}

.admin-drawer__user-name {
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-drawer__user-email {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============ MODE RÉDUIT (ICÔNES) ============ */
.admin-drawer--mini {
  .admin-drawer__header {
    padding: 16px 12px 4px;
  }

  .admin-drawer__brand {
    height: 52px;
    margin-bottom: 12px;
    border-radius: 12px;

    img {
      height: 34px;
    }
  }

  .admin-drawer__company {
    justify-content: center;
    padding: 8px;
  }

  .admin-drawer__company-info,
  .admin-nav__label,
  .admin-nav__chevron,
  .admin-nav__section,
  .admin-drawer__user-info {
    display: none;
  }

  .admin-nav {
    padding: 12px 14px 24px;
  }

  .admin-nav__item {
    justify-content: center;
    padding: 12px 0;
    margin-bottom: 6px;
  }

  .admin-nav__icon,
  .admin-nav__mdi {
    width: 22px;
    height: 22px;
  }

  .admin-drawer__user {
    justify-content: center;
    margin: 12px 10px;
    padding: 10px 0;
  }
}

/* ============ APP BAR ============ */
.admin-appbar {
  border-bottom: 1px solid $line !important;

  ::v-deep .v-toolbar__content {
    padding: 0 20px;
    gap: 8px;
  }
}

.admin-appbar__toggle {
  width: 40px;
  height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px solid $line;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  span {
    display: block;
    width: 16px;
    height: 2px;
    border-radius: 2px;
    background: $navy;
  }

  &:hover {
    border-color: rgba(21, 158, 114, 0.4);
    background: rgba(21, 158, 114, 0.05);
  }
}

.admin-appbar__heading {
  margin-left: 12px;
  min-width: 0;
}

.admin-appbar__section {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $green;
}

.admin-appbar__title {
  font-size: 17px;
  font-weight: 800;
  color: $navy;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-appbar__btn {
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid $line;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(21, 158, 114, 0.4);
  }
}

.admin-appbar__dot {
  position: absolute;
  top: 9px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $green;
  box-shadow: 0 0 0 2px #ffffff;
}

.admin-appbar__profile {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 4px;
  padding: 3px 14px 3px 3px;
  border: 1px solid $line;
  border-radius: 999px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(21, 158, 114, 0.4);
  }
}

.admin-appbar__avatar {
  background: linear-gradient(135deg, $green, $navy);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
}

.admin-appbar__name {
  font-size: 13px;
  font-weight: 700;
  color: $ink;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============ POPUPS ============ */
.admin-pop {
  background: #ffffff;
  border: 1px solid $line;
  border-radius: 14px;
  box-shadow: 0 20px 40px -12px rgba(12, 36, 97, 0.2);
  overflow: hidden;
}

.admin-pop__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid $line;
}

.admin-pop__title {
  font-size: 15px;
  font-weight: 800;
  color: $navy;
}

.admin-pop__count {
  font-size: 12px;
  font-weight: 700;
  color: $green;
  background: rgba(21, 158, 114, 0.1);
  padding: 2px 10px;
  border-radius: 999px;
}

.admin-pop__empty {
  padding: 36px 18px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}

.admin-pop__list {
  max-height: 380px;
  overflow-y: auto;
}

.admin-notif {
  display: flex;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid $line;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $soft;
  }
}

.admin-notif__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: $green;
}

.admin-notif__title {
  font-size: 13px;
  font-weight: 800;
  color: $ink;
  margin-bottom: 2px;
}

.admin-notif__text {
  font-size: 13px;
  line-height: 1.5;
  color: $muted;
}

.admin-pop__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  border-bottom: 1px solid $line;
}

.admin-pop__user-info {
  min-width: 0;
}

.admin-pop__user-name {
  font-size: 14px;
  font-weight: 800;
  color: $navy;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-pop__user-email {
  font-size: 12px;
  color: $muted;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-pop__menu {
  padding: 8px;
}

.admin-pop__link {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: $ink;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: $soft;
  }

  &--danger {
    color: $danger;

    &:hover {
      background: rgba(239, 68, 68, 0.06);
    }
  }
}

.admin-pop__lang {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 700;
  color: $ink;
}

.admin-lang {
  display: flex;
  gap: 4px;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 8px;
}

.admin-lang__opt {
  padding: 4px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-size: 12px;
  font-weight: 800;
  color: $muted;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    color: $green;
  }

  &--active {
    background: $green;
    color: #ffffff;

    &:hover {
      color: #ffffff;
    }
  }
}

.admin-pop__foot {
  padding: 8px;
  border-top: 1px solid $line;
}

/* ============ DÉCONNEXION ============ */
.admin-logout {
  background: #ffffff;
  border-radius: 18px;
  padding: 32px 28px 28px;
  text-align: center;
}

.admin-logout__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.1);
}

.admin-logout__title {
  font-size: 19px;
  font-weight: 900;
  color: $navy;
  margin-bottom: 8px;
}

.admin-logout__text {
  font-size: 14px;
  color: $muted;
  margin-bottom: 24px;
}

.admin-logout__actions {
  display: flex;
  gap: 12px;
}

.admin-logout__btn {
  flex: 1;
  height: 44px;
  border: 1px solid $line;
  border-radius: 10px;
  background: #ffffff;
  font-size: 14px;
  font-weight: 800;
  color: $ink;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: $soft;
  }

  &--danger {
    background: $danger;
    border-color: $danger;
    color: #ffffff;

    &:hover {
      background: #dc2626;
      box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
    }
  }
}

@media (max-width: 600px) {
  .admin-appbar ::v-deep .v-toolbar__content {
    padding: 0 12px;
  }

  .admin-appbar__name {
    display: none;
  }

  .admin-appbar__profile {
    padding: 3px;
  }

  .admin-appbar__title {
    font-size: 15px;
  }
}
</style>
